import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Fade,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AssessmentOutlined,
  Cached,
  FilterAltOutlined,
  ReplayOutlined,
  TimerOutlined,
  ArrowDownward,
  ArrowUpward,
  Remove,
} from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FilterBar from "../../../components/topBarFilter";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import useStyles from "./styles";
import DonutChartComponent from "../6.0-suporte/dashAtendimentos/charts/donutChartComponent";
import {
  ChartCard,
  EstadoApi,
  MetricCard,
  ReportTabSkeleton,
  SectionTitle,
  SemaforoLegend,
  semaforoCardColor,
  formatInt,
  formatPct,
  formatTempo,
  tableBodyCell,
  tableHeadCell,
} from "./shared";
import { pctColor } from "./analytics";
import { MicrogestaoAtendimentoTab, MicrogestaoCampoTab } from "./microgestao";
import { TendenciasCausaRaizTab } from "./tendenciasCausaRaiz";
import { relatorioWithController } from "./controller";
import {
  DEFAULT_RELATORIO_WITH_TAB,
  RELATORIO_WITH_BASE_PATH,
  RELATORIO_WITH_TABS,
  getRelatorioWithTabByPath,
  getRelatorioWithTabByValue,
} from "./model";

/* --------------------------- subcomponents -------------------------- */
// Donut de reincidência de UM mês (reincidente vs não reincidente).
function DonutReincidencia({
  styles,
  theme,
  titulo,
  base = 0,
  sim = 0,
  pct = 0,
}) {
  const temDados = (base || 0) > 0;
  const nao = Math.max((base || 0) - (sim || 0), 0);
  const donutData = [
    { name: "Reincidente", value: sim, color: theme.palette.error.main },
    { name: "Não reincidente", value: nao, color: theme.palette.success.main },
  ];

  return (
    <ChartCard
      styles={styles}
      theme={theme}
      noData={!temDados}
      noDataMessage="Sem base suficiente neste mês."
      emptyHeight={200}
    >
      <Box sx={{ height: 200, position: "relative", overflow: "hidden" }}>
        <DonutChartComponent
          data={donutData}
          title={`${titulo} — ${formatPct(pct)}`}
          loading={false}
          type={0}
        />
      </Box>
    </ChartCard>
  );
}

// Lista de barras horizontais (bullet) — uma linha por mês. Escala para
// muitos meses sem virar mosaico de donuts; comparação por barras alinhadas.
function BarrasReincidencia({ styles, theme, meses }) {
  const temDados = meses.some((m) => (m.base || 0) > 0);
  return (
    <ChartCard
      styles={styles}
      theme={theme}
      title="Reincidência por mês"
      subtitle="Janela de 90 dias — base → reincidentes"
      topRight={<SemaforoLegend theme={theme} />}
      noData={!temDados}
      noDataMessage="Sem base suficiente no período."
    >
      <Stack sx={{ gap: "2px", mt: "4px" }}>
        {meses.map((m, idx) => {
          const pct = Number(m.pct || 0);
          const cor = pctColor(theme, pct);
          const largura = `${Math.min(Math.max(pct * 100, 0), 100)}%`;
          return (
            <Box
              key={m.label}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(48px, 64px) 1fr auto",
                alignItems: "center",
                gap: "12px",
                py: "9px",
                borderBottom:
                  idx < meses.length - 1
                    ? `1px solid ${alpha(theme.palette.text.primary, 0.06)}`
                    : "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                }}
              >
                {m.label}
              </Typography>
              <Tooltip
                title={`${formatPct(pct)} de reincidência`}
                arrow
                placement="top"
              >
                <Box sx={{ ...styles.progressTrack, height: 10 }}>
                  <Box
                    sx={{
                      ...styles.progressBar,
                      width: largura,
                      backgroundColor: cor,
                      transition: "width .5s ease",
                    }}
                  />
                </Box>
              </Tooltip>
              <Stack alignItems="flex-end" sx={{ minWidth: 92 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: cor,
                  }}
                >
                  {formatPct(pct)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: theme.palette.text.secondary,
                    lineHeight: 1.2,
                  }}
                >
                  {formatInt(m.base)} → {formatInt(m.sim)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </ChartCard>
  );
}

// Heatmap mês × janela (30/60/90): mostra as três janelas de uma vez, com
// células tingidas pelo semáforo. Muito compacto e denso para muitos meses.
function HeatmapReincidencia({ styles, theme, meses }) {
  const cols = [
    { key: "d30", label: "30 dias" },
    { key: "d60", label: "60 dias" },
    { key: "d90", label: "90 dias" },
  ];
  const temDados = meses.some((m) =>
    cols.some((c) => (m[c.key]?.base || 0) > 0),
  );
  return (
    <ChartCard
      styles={styles}
      theme={theme}
      title="Mapa de reincidência (mês × janela)"
      subtitle="Percentual por mês e janela — cor pelo semáforo"
      topRight={<SemaforoLegend theme={theme} />}
      noData={!temDados}
      noDataMessage="Sem base suficiente no período."
    >
      <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: "6px",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  ...tableHeadCell(theme, "left"),
                  width: "28%",
                  borderBottom: "none",
                }}
              >
                Mês
              </Box>
              {cols.map((c) => (
                <Box
                  key={c.key}
                  component="th"
                  sx={{
                    ...tableHeadCell(theme, "center"),
                    width: "24%",
                    borderBottom: "none",
                  }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {meses.map((m) => (
              <Box component="tr" key={m.mes}>
                <Box
                  component="td"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.mes}
                </Box>
                {cols.map((c) => {
                  const cell = m[c.key];
                  const pct = Number(cell?.pct || 0);
                  const cor = pctColor(theme, pct);
                  const vazio = !cell || (cell.base || 0) === 0;
                  return (
                    <Box key={c.key} component="td" sx={{ p: 0 }}>
                      {vazio ? (
                        <Box
                          sx={{
                            textAlign: "center",
                            color: theme.palette.text.disabled,
                            fontSize: 13,
                            py: "14px",
                          }}
                        >
                          —
                        </Box>
                      ) : (
                        <Tooltip
                          title={`${formatInt(cell.sim)} de ${formatInt(cell.base)} reincidentes`}
                          arrow
                          placement="top"
                        >
                          <Box
                            sx={{
                              borderRadius: "10px",
                              backgroundColor: alpha(cor, 0.14),
                              border: `1px solid ${alpha(cor, 0.32)}`,
                              textAlign: "center",
                              py: "10px",
                              px: "6px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 15,
                                fontWeight: 800,
                                lineHeight: 1.1,
                                color: cor,
                              }}
                            >
                              {formatPct(pct)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.3,
                              }}
                            >
                              {formatInt(cell.sim)}/{formatInt(cell.base)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </ChartCard>
  );
}

/* ------------------------------- tabs ------------------------------- */
function ResumoTab({
  styles,
  theme,
  consolidado,
  loadingConsolidado,
  motivos,
  loadingMotivos,
  regionais,
  loadingRegional,
}) {
  // Cada bloco vem de uma API independente, com seu próprio loading.
  const reinc = consolidado?.reincidencia;
  const base = consolidado?.totalBase || 0;
  const temConsolidado = !!reinc?.r90;

  const motAbertura = motivos?.abertura || [];
  const motFechamento = motivos?.fechamento || [];
  const regionaisRows = regionais || [];
  const temMotivos = !!(motAbertura.length || motFechamento.length);
  const temRegional = !!regionaisRows.length;

  // Visualização da reincidência mensal: "donut" (atual), "barras" (lista
  // horizontal, melhor p/ muitos meses) ou "heatmap" (mês × janela).
  const [mesView, setMesView] = useState("barras");

  const accents = {
    base: theme.palette.primary.main,
    r90: theme.palette.error.main,
    r60: theme.palette.warning.main,
    r30: theme.palette.success.main,
    tempo: theme.palette.info.main,
  };

  // Meses presentes no período (derivados da janela de 90 dias).
  const mesesDonut = reinc?.r90?.meses || [];

  // Matriz mês × janela (30/60/90) para o heatmap — junta as três janelas
  // pelo rótulo do mês; cada célula = { base, sim, pct } ou null.
  const mesesMatriz = mesesDonut.map((m) => {
    const cell = (bloco) =>
      reinc?.[bloco]?.meses?.find((x) => x.label === m.label) || null;
    return {
      mes: m.label,
      d30: cell("r30"),
      d60: cell("r60"),
      d90: cell("r90"),
    };
  });

  // Evolução mensal: junta os meses das três janelas em uma série por mês.
  const evolucaoMensal = temConsolidado
    ? mesesDonut.map((m) => {
        const mes = m.label;
        const pctDe = (bloco) =>
          reinc[bloco]?.meses?.find((x) => x.label === mes)?.pct ?? null;
        const toNum = (p) => (p != null ? +(p * 100).toFixed(1) : null);
        return {
          mes,
          parcial: false,
          "Reincidência 90": toNum(pctDe("r90")),
          "Reincidência 60": toNum(pctDe("r60")),
          "Reincidência 30": toNum(pctDe("r30")),
        };
      })
    : [];
  const temEvolucao = evolucaoMensal.some((item) =>
    ["Reincidência 90", "Reincidência 60", "Reincidência 30"].some(
      (key) => item[key] != null,
    ),
  );

  const motivoColumns = [
    { key: "d30", pkey: "p30", label: "30 dias", compareTo: "p60" },
    { key: "d60", pkey: "p60", label: "60 dias", compareTo: "p90" },
    { key: "d90", pkey: "p90", label: "90 dias" },
  ];

  const regionalColumns = [
    { key: "r30", pkey: "p30", label: "30 dias" },
    { key: "r60", pkey: "p60", label: "60 dias" },
    { key: "r90", pkey: "p90", label: "90 dias" },
  ];

  const renderRegionalTable = () => (
    <ChartCard
      styles={styles}
      theme={theme}
      title="Reincidência por regional"
      subtitle="Base, reincidentes e percentual por janela"
      loading={loadingRegional && !temRegional}
      noData={!loadingRegional && !temRegional}
      noDataTitle="Sem dados por regional"
      noDataMessage="Não foram encontradas regionais vinculadas aos serviços no período."
    >
      <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{ ...tableHeadCell(theme, "left"), width: "34%" }}
              >
                Regional
              </Box>
              <Box
                component="th"
                sx={{ ...tableHeadCell(theme, "right"), width: "12%" }}
              >
                Base
              </Box>
              {regionalColumns.map((c) => (
                <Box
                  key={c.key}
                  component="th"
                  sx={{ ...tableHeadCell(theme, "right"), width: "18%" }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {regionaisRows.slice(0, 8).map((row, idx) => (
              <Box component="tr" key={row.regional || idx}>
                <Box
                  component="td"
                  sx={{
                    ...tableBodyCell(theme, "left", idx),
                    width: "34%",
                    maxWidth: 0,
                  }}
                >
                  <Tooltip title={row.regional || ""} arrow placement="top">
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.regional || "—"}
                    </Box>
                  </Tooltip>
                </Box>
                <Box component="td" sx={tableBodyCell(theme, "right", idx)}>
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, fontSize: 13 }}
                  >
                    {formatInt(row.base)}
                  </Typography>
                </Box>
                {regionalColumns.map((c) => (
                  <Box
                    key={c.key}
                    component="td"
                    sx={tableBodyCell(theme, "right", idx)}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 700, fontSize: 13 }}
                    >
                      {formatInt(row[c.key])}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        ml: "6px",
                      }}
                    >
                      {formatPct(row[c.pkey])}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </ChartCard>
  );

  const renderMotivosTable = (titulo, linhas, accent, loading = false) => (
    <ChartCard
      styles={styles}
      theme={theme}
      title={titulo}
      subtitle="Top 5 motivos por janela"
      loading={loading}
      noData={!loading && !linhas.length}
      noDataTitle="Sem dados"
      noDataMessage="Não foram encontrados motivos para o período selecionado."
    >
      <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{ ...tableHeadCell(theme, "left"), width: "46%" }}
              >
                Motivo
              </Box>
              {motivoColumns.map((c) => (
                <Box
                  key={c.key}
                  component="th"
                  sx={{ ...tableHeadCell(theme, "right"), width: "18%" }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {linhas.map((row, idx) => (
              <Box component="tr" key={row.motivo}>
                <Box
                  component="td"
                  sx={{
                    ...tableBodyCell(theme, "left", idx),
                    width: "46%",
                    maxWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="8px"
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 22,
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 800,
                        backgroundColor: alpha(accent, 0.14),
                        color: accent,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Tooltip title={row.motivo || ""} arrow placement="top">
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.motivo}
                      </Box>
                    </Tooltip>
                  </Stack>
                </Box>
                {motivoColumns.map((c) => {
                  const val = row[c.pkey];
                  const compareVal = c.compareTo ? row[c.compareTo] : null;
                  let ArrowIcon = null;
                  let arrowColor = "inherit";

                  if (c.compareTo && val != null && compareVal != null) {
                    if (val < compareVal) {
                      ArrowIcon = ArrowDownward;
                      arrowColor = theme.palette.success.main;
                    } else if (val > compareVal) {
                      ArrowIcon = ArrowUpward;
                      arrowColor = theme.palette.error.main;
                    } else {
                      ArrowIcon = Remove;
                      arrowColor = theme.palette.text.secondary;
                    }
                  }

                  return (
                    <Box
                      key={c.key}
                      component="td"
                      sx={tableBodyCell(theme, "right", idx)}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 700, fontSize: 13 }}
                      >
                        {formatInt(row[c.key])}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          ml: "6px",
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                          }}
                        >
                          {formatPct(val)}
                        </Typography>
                        {ArrowIcon && (
                          <ArrowIcon
                            sx={{ color: arrowColor, fontSize: 14, ml: "2px" }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </ChartCard>
  );

  if (loadingConsolidado && !temConsolidado) {
    return <ReportTabSkeleton styles={styles} theme={theme} />;
  }

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 2, sm: 4, md: 6, lg: 10, xl: 10 }}
    >
      {!temConsolidado && (
        <Grid item xs={2} sm={4} md={6} lg={10}>
          <EstadoApi
            styles={styles}
            theme={theme}
            loading={loadingConsolidado}
            mensagem="Sem dados de reincidência"
            detalhe="A API não retornou dados para o período."
            altura={300}
          />
        </Grid>
      )}
      {temConsolidado && (
        <>
          {/* KPIs */}
          <Grid item xs={2} sm={2} md={2} lg={2}>
            <MetricCard
              styles={styles}
              theme={theme}
              icon={AssessmentOutlined}
              accent={accents.base}
              title="Total da base"
              value={formatInt(base)}
              helper="Tickets avaliados no período"
            />
          </Grid>
          <Grid item xs={2} sm={2} md={2} lg={2}>
            <MetricCard
              styles={styles}
              theme={theme}
              icon={ReplayOutlined}
              accent={accents.r90}
              paramsColor={semaforoCardColor(
                theme,
                pctColor,
                reinc.r90.consolidado.pct,
              )}
              title="Reincidência 90"
              value={formatPct(reinc.r90.consolidado.pct)}
              helper={`${formatInt(reinc.r90.consolidado.sim)} reincidentes`}
            />
          </Grid>
          <Grid item xs={2} sm={2} md={2} lg={2}>
            <MetricCard
              styles={styles}
              theme={theme}
              icon={ReplayOutlined}
              accent={accents.r60}
              paramsColor={semaforoCardColor(
                theme,
                pctColor,
                reinc.r60.consolidado.pct,
              )}
              title="Reincidência 60"
              value={formatPct(reinc.r60.consolidado.pct)}
              helper={`${formatInt(reinc.r60.consolidado.sim)} reincidentes`}
            />
          </Grid>
          <Grid item xs={2} sm={2} md={2} lg={2}>
            <MetricCard
              styles={styles}
              theme={theme}
              icon={ReplayOutlined}
              accent={accents.r30}
              paramsColor={semaforoCardColor(
                theme,
                pctColor,
                reinc.r30.consolidado.pct,
              )}
              title="Reincidência 30"
              value={formatPct(reinc.r30.consolidado.pct)}
              helper={`${formatInt(reinc.r30.consolidado.sim)} reincidentes`}
            />
          </Grid>

          <Grid item xs={2} sm={2} md={2} lg={2}>
            <MetricCard
              styles={styles}
              theme={theme}
              icon={TimerOutlined}
              accent={accents.tempo}
              title="Tempo médio (90)"
              value={formatTempo(reinc.r90.consolidado.tempo)}
              helper="Tempo médio de execução"
            />
          </Grid>

          {/* Reincidência por mês — seletor de visualização */}
          <Grid item xs={2} sm={4} md={6} lg={10}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <SectionTitle
                  theme={theme}
                  title="Reincidência por mês"
                  helper="Escolha a visualização — barras e heatmap escalam melhor para muitos meses"
                  icon={ReplayOutlined}
                />
              </Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mesView}
                onChange={(_e, v) => v && setMesView(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.5,
                    px: 1.5,
                    borderColor: alpha(theme.palette.text.primary, 0.16),
                  },
                }}
              >
                <ToggleButton value="donut">Donuts</ToggleButton>
                <ToggleButton value="barras">Barras</ToggleButton>
                <ToggleButton value="heatmap">Heatmap</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>

          {/* Donuts de reincidência — um por mês do período */}
          {mesView === "donut" && (
            <Grid item xs={2} sm={4} md={6} lg={10}>
              <Grid
                container
                spacing={1}
                columns={{ xs: 2, sm: 4, md: 6, lg: 6 }}
              >
                {mesesDonut.map((m) => (
                  <Grid item key={m.label} xs={2} sm={4} md={2} lg={2}>
                    <DonutReincidencia
                      styles={styles}
                      theme={theme}
                      titulo={m.label}
                      base={m.base}
                      sim={m.sim}
                      pct={m.pct}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {mesView === "barras" && (
            <Grid item xs={2} sm={4} md={6} lg={10} sx={{ minWidth: 0 }}>
              <BarrasReincidencia
                styles={styles}
                theme={theme}
                meses={mesesDonut}
              />
            </Grid>
          )}

          {mesView === "heatmap" && (
            <Grid item xs={2} sm={4} md={6} lg={10} sx={{ minWidth: 0 }}>
              <HeatmapReincidencia
                styles={styles}
                theme={theme}
                meses={mesesMatriz}
              />
            </Grid>
          )}

          {/* Evolução mensal */}
          <Grid
            item
            xs={2}
            sm={4}
            md={6}
            lg={10}
            sx={{ maxWidth: "100%", minWidth: 0, overflow: "hidden" }}
          >
            <ChartCard
              styles={styles}
              theme={theme}
              title="Evolução mensal da reincidência"
              subtitle="Percentual de reincidência por mês e janela"
              noData={!temEvolucao}
              noDataMessage="Não há pontos mensais suficientes para exibir a evolução."
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  ...styles.chartBox,
                  overflow: "hidden",
                  "& .recharts-responsive-container": {
                    maxWidth: "100%",
                  },
                  "& .recharts-wrapper, & .recharts-surface": {
                    maxWidth: "100%",
                  },
                }}
              >
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                  minWidth={0}
                  debounce={80}
                >
                  <BarChart
                    data={evolucaoMensal}
                    barGap={6}
                    margin={{ left: 0, right: 8 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      horizontal={true}
                      stroke={theme.palette.divider}
                      strokeDasharray="3"
                    />
                    <XAxis
                      dataKey="mes"
                      tickFormatter={(v) =>
                        evolucaoMensal.find((m) => m.mes === v)?.parcial
                          ? `${v}*`
                          : v
                      }
                      tick={{
                        fontSize: 12,
                        fill: theme.palette.text.secondary,
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{
                        fontSize: 12,
                        fill: theme.palette.text.secondary,
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => [`${value}%`, name]}
                      labelFormatter={(label) =>
                        evolucaoMensal.find((m) => m.mes === label)?.parcial
                          ? `${label} (parcial)`
                          : label
                      }
                      contentStyle={styles.chartTooltip}
                      cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    {[
                      { key: "Reincidência 90", color: accents.r90 },
                      { key: "Reincidência 60", color: accents.r60 },
                      { key: "Reincidência 30", color: accents.r30 },
                    ].map((serie) => (
                      <Bar
                        key={serie.key}
                        dataKey={serie.key}
                        fill={serie.color}
                        radius={[6, 6, 0, 0]}
                      >
                        {evolucaoMensal.map((d) => (
                          <Cell
                            key={d.mes}
                            fill={serie.color}
                            fillOpacity={d.parcial ? 0.4 : 1}
                          />
                        ))}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Typography sx={{ ...styles.cardSubtitle, mt: 1 }}>
                * Maio é um mês parcial (dados até 04/05) — comparar com
                cautela.
              </Typography>
            </ChartCard>
          </Grid>
        </>
      )}

      {/* Regionais */}
      <Grid item xs={2} sm={4} md={6} lg={10} sx={{ minWidth: 0 }}>
        {renderRegionalTable()}
      </Grid>

      {/* Top motivos */}
      <Grid item xs={2} sm={4} md={5} lg={5} sx={{ minWidth: 0 }}>
        {renderMotivosTable(
          "Motivos de Abertura",
          motAbertura,
          theme.palette.primary.main,
          loadingMotivos && !temMotivos,
        )}
      </Grid>
      <Grid item xs={2} sm={4} md={5} lg={5} sx={{ minWidth: 0 }}>
        {renderMotivosTable(
          "Motivos de Fechamento",
          motFechamento,
          theme.palette.warning.main,
          loadingMotivos && !temMotivos,
        )}
      </Grid>
    </Grid>
  );
}

// filtro de tempo (mesmas opções do dashAtendimentos)
export default function RelatorioWith() {
  const theme = useTheme();
  const styles = useStyles(theme);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab =
    getRelatorioWithTabByPath(location.pathname) || DEFAULT_RELATORIO_WITH_TAB;
  const tab = activeTab.value;
  const requestControllerRef = useRef(null);

  // ---- filtro de data + refresh (mesmo padrão do dashAtendimentos) ----
  const [openFilter, setOpenFilter] = useState(false);
  // Filtro de tempo em escala MENSAL (intervalo mês inicial → final).
  // Padrão: últimos 3 meses (mês atual + 2 anteriores).
  const [mesInicial, setMesInicial] = useState(
    dayjs().subtract(2, "month").startOf("month"),
  );
  const [mesFinal, setMesFinal] = useState(dayjs().startOf("month"));
  const [filterData, setFilterData] = useState({
    initialDate: dayjs().subtract(2, "month").startOf("month").toDate(),
    finalDate: dayjs().endOf("month").toDate(),
    filtroRegional: [],
    filtroTecnico: [],
    filtroAtendente: [],
  });

  const [allRegionalFilter, setAllRegionalFilter] = useState([]);
  const [filtroRegional, setFiltroRegional] = useState([]);
  const clearRegionalRef = useRef(false);
  const prevRegionalRef = useRef([]);

  const [allTecnicoFilter, setAllTecnicoFilter] = useState([]);
  const [filtroTecnico, setFiltroTecnico] = useState([]);
  const clearTecnicoRef = useRef(false);
  const prevTecnicoRef = useRef([]);

  const [allAtendenteFilter, setAllAtendenteFilter] = useState([]);
  const [filtroAtendente, setFiltroAtendente] = useState([]);
  const clearAtendenteRef = useRef(false);
  const prevAtendenteRef = useRef([]);

  const [loadingFilter, setLoadingFilter] = useState(false);

  useEffect(() => {
    const fetchFiltros = async () => {
      setLoadingFilter(true);
      try {
        const response = await relatorioWithController.getFiltros();
        const data = response?.data || response || {};
        if (data.regional && Array.isArray(data.regional)) {
          setAllRegionalFilter(data.regional);
        } else {
          setAllRegionalFilter([]);
        }

        if (data.tecnicos && Array.isArray(data.tecnicos)) {
          setAllTecnicoFilter(data.tecnicos);
        } else {
          setAllTecnicoFilter([]);
        }

        if (data.atendentes && Array.isArray(data.atendentes)) {
          setAllAtendenteFilter(data.atendentes);
        } else {
          setAllAtendenteFilter([]);
        }
      } catch (error) {
        console.error("Erro ao carregar filtros:", error);
      } finally {
        setLoadingFilter(false);
      }
    };
    fetchFiltros();
  }, []);

  useEffect(() => {
    if (!allRegionalFilter?.length) return;
    setFiltroRegional((prevSelecionados) =>
      prevSelecionados
        ?.map((sel) => allRegionalFilter?.find((f) => f?.id === sel?.id))
        ?.filter(Boolean),
    );
  }, [allRegionalFilter]);

  useEffect(() => {
    if (!allTecnicoFilter?.length) return;
    setFiltroTecnico((prevSelecionados) =>
      prevSelecionados
        ?.map((sel) => allTecnicoFilter?.find((f) => f?.id === sel?.id))
        ?.filter(Boolean),
    );
  }, [allTecnicoFilter]);

  useEffect(() => {
    if (clearRegionalRef.current && filtroRegional?.length === 0) {
      setFilterData((prev) => ({ ...prev, filtroRegional: [] }));
      clearRegionalRef.current = false;
    }
  }, [filtroRegional]);

  useEffect(() => {
    if (clearTecnicoRef.current && filtroTecnico?.length === 0) {
      setFilterData((prev) => ({ ...prev, filtroTecnico: [] }));
      clearTecnicoRef.current = false;
    }
  }, [filtroTecnico]);

  useEffect(() => {
    if (!allAtendenteFilter?.length) return;
    setFiltroAtendente((prevSelecionados) =>
      prevSelecionados
        ?.map((sel) => allAtendenteFilter?.find((f) => f?.id === sel?.id))
        ?.filter(Boolean),
    );
  }, [allAtendenteFilter]);

  useEffect(() => {
    if (clearAtendenteRef.current && filtroAtendente?.length === 0) {
      setFilterData((prev) => ({ ...prev, filtroAtendente: [] }));
      clearAtendenteRef.current = false;
    }
  }, [filtroAtendente]);

  const handleOnCloseFilter = (prevFilter, currFilter, filterKey) => {
    const prevIds = prevFilter.current?.map((r) => r?.id)?.sort();
    const currIds = currFilter?.map((r) => r?.id)?.sort();
    const changed = JSON?.stringify(prevIds) !== JSON?.stringify(currIds);

    if (changed) {
      setFilterData((prev) => ({ ...prev, [filterKey]: currFilter }));
    }
  };

  // Endpoints independentes (estilo dashAtendimentos): cada bloco carrega só.
  const [consolidado, setConsolidado] = useState(null);
  const [motivos, setMotivos] = useState(null);
  const [regionais, setRegionais] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [atendentes, setAtendentes] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loadingConsolidado, setLoadingConsolidado] = useState(true);
  const [loadingMotivos, setLoadingMotivos] = useState(true);
  const [loadingRegional, setLoadingRegional] = useState(true);
  const [loadingTecnicos, setLoadingTecnicos] = useState(true);
  const [loadingAtendimento, setLoadingAtendimento] = useState(false);
  const loadingCronometro =
    tab === 0
      ? loadingConsolidado || loadingMotivos || loadingRegional
      : tab === 1
        ? loadingTecnicos
        : tab === 2
          ? loadingAtendimento
          : false;

  useEffect(() => {
    if (location.pathname === RELATORIO_WITH_BASE_PATH) {
      navigate(DEFAULT_RELATORIO_WITH_TAB.path, { replace: true });
      return;
    }

    if (!getRelatorioWithTabByPath(location.pathname)) {
      navigate(DEFAULT_RELATORIO_WITH_TAB.path, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }

    const controller = new AbortController();
    requestControllerRef.current = controller;
    const { signal } = controller;

    const handleError = (name, error, fallback) => {
      if (signal.aborted) return;
      console.error(`Erro em ${name}:`, error);
      fallback();
    };

    const loadResumo = async () => {
      setLoadingConsolidado(true);
      setLoadingMotivos(true);
      setLoadingRegional(true);

      await Promise.all([
        relatorioWithController
          .getConsolidado(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setConsolidado(data || null);
          })
          .catch((error) =>
            handleError("reincidencia/consolidado", error, () =>
              setConsolidado(null),
            ),
          )
          .finally(() => {
            if (!signal.aborted) setLoadingConsolidado(false);
          }),
        relatorioWithController
          .getMotivos(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setMotivos(data || null);
          })
          .catch((error) =>
            handleError("reincidencia/motivos", error, () => setMotivos(null)),
          )
          .finally(() => {
            if (!signal.aborted) setLoadingMotivos(false);
          }),
        relatorioWithController
          .getRegional(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setRegionais(Array.isArray(data) ? data : []);
          })
          .catch((error) =>
            handleError("reincidencia/regional", error, () => setRegionais([])),
          )
          .finally(() => {
            if (!signal.aborted) setLoadingRegional(false);
          }),
      ]);
    };

    const loadCampo = async () => {
      setLoadingTecnicos(true);
      await Promise.all([
        relatorioWithController
          .getTecnicos(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setTecnicos(data || []);
          })
          .catch((error) =>
            handleError("reincidencia/tecnicos", error, () => setTecnicos([])),
          ),
        // Regional alimenta o mapa com valores reais (casados por nome às cidades).
        relatorioWithController
          .getRegional(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setRegionais(Array.isArray(data) ? data : []);
          })
          .catch((error) =>
            handleError("reincidencia/regional", error, () => setRegionais([])),
          ),
      ]).finally(() => {
        if (!signal.aborted) setLoadingTecnicos(false);
      });
    };

    const loadAtendimento = async () => {
      setLoadingAtendimento(true);
      await Promise.all([
        relatorioWithController
          .getAtendimento(filterData, signal)
          .then((data) => {
            if (!signal.aborted)
              setAtendentes(
                Array.isArray(data) ? data : data?.atendentes || [],
              );
          })
          .catch((error) =>
            handleError("reincidencia/atendimento", error, () =>
              setAtendentes([]),
            ),
          ),
        relatorioWithController
          .getSetor(filterData, signal)
          .then((data) => {
            if (!signal.aborted) setSetores(Array.isArray(data) ? data : []);
          })
          .catch((error) =>
            handleError("reincidencia/setor", error, () => setSetores([])),
          ),
      ]).finally(() => {
        if (!signal.aborted) setLoadingAtendimento(false);
      });
    };

    const loaders = {
      0: loadResumo,
      1: loadCampo,
      2: loadAtendimento,
    };

    loaders[tab]?.();

    return () => controller.abort();
  }, [filterData, tab]);

  // Converte o intervalo de meses em { initialDate (início do mês inicial),
  // finalDate (fim do mês final, limitado a agora) }.
  const periodoMeses = (ini, fim) => {
    const start = dayjs(ini).startOf("month");
    const endRaw = dayjs(fim).endOf("month");
    const end = endRaw.isAfter(dayjs()) ? dayjs() : endRaw;
    return { initialDate: start.toDate(), finalDate: end.toDate() };
  };
  const handleMesInicial = (value) => {
    if (!value || !value.isValid()) return;
    const fim = value.isAfter(mesFinal) ? value : mesFinal;
    setMesInicial(value);
    setMesFinal(fim);
    setFilterData((prev) => ({ ...prev, ...periodoMeses(value, fim) }));
  };
  const handleMesFinal = (value) => {
    if (!value || !value.isValid()) return;
    const ini = value.isBefore(mesInicial) ? value : mesInicial;
    setMesFinal(value);
    setMesInicial(ini);
    setFilterData((prev) => ({ ...prev, ...periodoMeses(ini, value) }));
  };
  const handleRefresh = () => {
    setFilterData((prev) => ({
      ...prev,
      ...periodoMeses(mesInicial, mesFinal),
    }));
  };

  const actions = [
    {
      icon: <FilterAltOutlined />,
      name: "Filtro",
      onClick: () => setOpenFilter(true),
    },
    {
      icon: <Cached />,
      name: "Atualizar",
      onClick: handleRefresh,
    },
  ];

  return (
    <>
      {" "}
      <FilterBar
        actions={actions}
        type={1}
        setOpenFilter={setOpenFilter}
        openFilter={openFilter}
        selectorComponentLeft={
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: "100%",
              "@media (max-width:1349px)": { flexDirection: "column" },
            }}
          >
            <Box
              sx={styles.autocompleteFilter || { minWidth: 200, width: "100%" }}
            >
              <Autocomplete
                disableCloseOnSelect
                multiple
                options={allRegionalFilter || []}
                onOpen={() => {
                  prevRegionalRef.current = [...filtroRegional];
                }}
                onClose={() => {
                  handleOnCloseFilter(
                    prevRegionalRef,
                    filtroRegional,
                    "filtroRegional",
                  );
                }}
                getOptionLabel={(option) => option?.tagName || "Sem Nome"}
                size="small"
                disabled={loadingFilter}
                value={filtroRegional}
                onChange={(e, newValue, reason) => {
                  if (reason === "clear") {
                    clearRegionalRef.current = true;
                    setFiltroRegional([]);
                    return;
                  }
                  setFiltroRegional(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regional"
                    sx={styles.autocompleteText || {}}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      style={{ marginRight: 8 }}
                    />
                    {option?.tagName}
                  </li>
                )}
                renderTags={(selected, getTagProps) => (
                  <Tooltip
                    title={
                      selected.length ? (
                        <Box>
                          {selected.map((regional) => (
                            <Typography key={regional.id} sx={{ fontSize: 10 }}>
                              {regional?.tagName}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        "Nenhum regional selecionado"
                      )
                    }
                  >
                    <Box>
                      {selected.length === 1 ? (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: "100%" }}
                        >
                          {selected[0]?.tagName}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ overflow: "hidden", maxWidth: "100%" }}
                        >
                          {selected?.length} regionais selecionados
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                )}
              />
            </Box>
            <Box
              sx={styles.autocompleteFilter || { minWidth: 200, width: "100%" }}
            >
              <Autocomplete
                disableCloseOnSelect
                multiple
                options={allTecnicoFilter || []}
                onOpen={() => {
                  prevTecnicoRef.current = [...filtroTecnico];
                }}
                onClose={() => {
                  handleOnCloseFilter(
                    prevTecnicoRef,
                    filtroTecnico,
                    "filtroTecnico",
                  );
                }}
                getOptionLabel={(option) => option?.name || "Sem Nome"}
                size="small"
                disabled={loadingFilter}
                value={filtroTecnico}
                onChange={(e, newValue, reason) => {
                  if (reason === "clear") {
                    clearTecnicoRef.current = true;
                    setFiltroTecnico([]);
                    return;
                  }
                  setFiltroTecnico(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Técnico"
                    sx={styles.autocompleteText || {}}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      style={{ marginRight: 8 }}
                    />
                    {option?.name}
                  </li>
                )}
                renderTags={(selected, getTagProps) => (
                  <Tooltip
                    title={
                      selected.length ? (
                        <Box>
                          {selected.map((tec) => (
                            <Typography key={tec.id} sx={{ fontSize: 10 }}>
                              {tec?.name}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        "Nenhum técnico selecionado"
                      )
                    }
                  >
                    <Box>
                      {selected.length === 1 ? (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: "100%" }}
                        >
                          {selected[0]?.name || "Sem Nome"}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ overflow: "hidden", maxWidth: "100%" }}
                        >
                          {selected?.length} técnicos selecionados
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                )}
              />
            </Box>
            <Box
              sx={styles.autocompleteFilter || { minWidth: 200, width: "100%" }}
            >
              <Autocomplete
                disableCloseOnSelect
                multiple
                options={allAtendenteFilter || []}
                onOpen={() => {
                  prevAtendenteRef.current = [...filtroAtendente];
                }}
                onClose={() => {
                  handleOnCloseFilter(
                    prevAtendenteRef,
                    filtroAtendente,
                    "filtroAtendente",
                  );
                }}
                getOptionLabel={(option) => option?.name || "Sem Nome"}
                size="small"
                disabled={loadingFilter}
                value={filtroAtendente}
                onChange={(e, newValue, reason) => {
                  if (reason === "clear") {
                    clearAtendenteRef.current = true;
                    setFiltroAtendente([]);
                    return;
                  }
                  setFiltroAtendente(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Atendente"
                    sx={styles.autocompleteText || {}}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      style={{ marginRight: 8 }}
                    />
                    {option?.name}
                  </li>
                )}
                renderTags={(selected, getTagProps) => (
                  <Tooltip
                    title={
                      selected.length ? (
                        <Box>
                          {selected.map((atendente) => (
                            <Typography
                              key={atendente.id}
                              sx={{ fontSize: 10 }}
                            >
                              {atendente?.name}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        "Nenhum atendente selecionado"
                      )
                    }
                  >
                    <Box>
                      {selected.length === 1 ? (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: "100%" }}
                        >
                          {selected[0]?.name || "Sem Nome"}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ overflow: "hidden", maxWidth: "100%" }}
                        >
                          {selected?.length} atendentes selecionados
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                )}
              />
            </Box>
            {loadingFilter && <LinearProgress />}
          </Box>
        }
        selectorComponentRight={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Mês inicial"
                views={["year", "month"]}
                openTo="month"
                format="MM/YYYY"
                value={mesInicial}
                maxDate={mesFinal}
                onChange={handleMesInicial}
                slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
              />
              <DatePicker
                label="Mês final"
                views={["year", "month"]}
                openTo="month"
                format="MM/YYYY"
                value={mesFinal}
                minDate={mesInicial}
                maxDate={dayjs()}
                onChange={handleMesFinal}
                slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
              />
            </LocalizationProvider>
            <Button
              size="small"
              variant="outlined"
              disabled={loadingCronometro}
              onClick={handleRefresh}
              sx={{
                ...styles.buttonRefresh,
                "& .rotating": { animation: "spin 1s linear infinite" },
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(-360deg)" },
                },
              }}
            >
              <Cached
                fontSize="small"
                sx={{ color: theme.palette.text.main }}
                className={loadingCronometro ? "rotating" : ""}
              />
            </Button>
          </Box>
        }
      />
      <Box sx={styles.container}>
        <Paper
          sx={styles.tabsPaper}
          elevation={theme.palette.mode === "dark" ? 0 : 3}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              minWidth: 0,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, value) => {
                const nextTab = getRelatorioWithTabByValue(value);
                navigate(nextTab.path);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ ...styles.tabs, flex: 1, minWidth: 0 }}
            >
              {RELATORIO_WITH_TABS.map((t) => (
                <Tab key={t.value} label={t.label} value={t.value} />
              ))}
            </Tabs>
            <SemaforoLegend
              theme={theme}
              sx={{
                display: { xs: "none", md: "flex" },
                flexShrink: 0,
                flexWrap: "nowrap",
                pr: 2,
              }}
            />
          </Box>
        </Paper>

        <Fade in appear key={tab} timeout={280}>
          <Box sx={{ minWidth: 0 }}>
            {tab === 0 && (
              <ResumoTab
                styles={styles}
                theme={theme}
                consolidado={consolidado}
                loadingConsolidado={loadingConsolidado}
                motivos={motivos}
                loadingMotivos={loadingMotivos}
                regionais={regionais}
                loadingRegional={loadingRegional}
              />
            )}
            {tab === 1 && (
              <MicrogestaoCampoTab
                styles={styles}
                theme={theme}
                tecnicos={tecnicos}
                regionais={regionais}
                loading={loadingTecnicos}
              />
            )}
            {tab === 2 && (
              <MicrogestaoAtendimentoTab
                styles={styles}
                theme={theme}
                atendentes={atendentes}
                setores={setores}
                loading={loadingAtendimento}
              />
            )}
            {tab === 3 && (
              <TendenciasCausaRaizTab styles={styles} theme={theme} />
            )}
          </Box>
        </Fade>
      </Box>
    </>
  );
}
